import { useMemo } from 'react';
import { Outlet } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box } from '@mui/material';
import { useActiveAdminTab } from './admin/useActiveAdminTab';
import { Breadcrumbs, type BreadcrumbItem } from '@/components/common';
import { usePageTitle } from '@/hooks/usePageTitle';

/**
 * Outlet shell for the admin area.
 *
 * The admin navigation itself no longer lives here — while under `/admin` the
 * app shell (`Layout`) swaps its primary `SideMenu` for `AdminSideMenu`, so the
 * grouped tool list sits in the one-and-only sidebar and this content pane spans
 * the full width. All this shell adds is the `Admin › Category › Tool`
 * breadcrumb above the active tool; selection stays URL-driven (see
 * `useActiveAdminTab`), so deep links keep working. At the bare `/admin` landing
 * no tool is active and the outlet renders the landing grid without a crumb.
 */
export function AdminLayout() {
  usePageTitle('Admin');
  const { t } = useTranslation();
  const { category, tab } = useActiveAdminTab();

  const crumbs = useMemo<BreadcrumbItem[] | null>(() => {
    if (!tab || !category) return null;
    return [
      { label: t('admin.layout.pageTitle'), to: '/admin' },
      { label: t(category.labelKey) },
      { label: t(tab.labelKey) },
    ];
  }, [category, tab, t]);

  return (
    <>
      {crumbs && (
        <Box sx={{ mb: 2 }}>
          <Breadcrumbs items={crumbs} />
        </Box>
      )}
      <Outlet />
    </>
  );
}

export default AdminLayout;
